import { nonNull } from '../../utils/filters/non-null';
import { onlyUnique } from '../../utils/filters/only-unique';
import { takeInParentheses } from '../../utils/formatters/value-mappers';
import {
  BasicSeries,
  OutlookLabel,
  Rating,
  RatingOutlook,
  RatingStatus,
  Series
} from './../../types/deal.types';

export interface EnhancedRating extends Rating {
  enhancement?: string;
}

export interface EnhancedFullRating {
  rating: EnhancedRating;
  underlyingRating: Rating;
}

export interface RatingAgency {
  key: string;
  name: string;
  label: string;
}

export const RATING_AGENCIES = [
  {
    key: 'moody',
    name: "Moody's",
    label: "Moody's"
  },
  {
    key: 'sp',
    name: "Standard & Poor's",
    label: 'S&P'
  },
  {
    key: 'fitch',
    name: 'Fitch',
    label: 'Fitch'
  },
  {
    key: 'kroll',
    name: 'Kroll',
    label: 'Kroll'
  }
];

const EQUIVALENCE_FIELDS = [
  'agency',
  'rating',
  'shortTerm',
  'status',
  'enhancement'
];

export function isEquivalent(a: EnhancedRating, b: EnhancedRating) {
  if (!!a && !!b) {
    for (const key of EQUIVALENCE_FIELDS) {
      if (a[key] !== b[key]) {
        return false;
      }
    }
    return true;
  }
  return !a && !b;
}

export function isNotRated(rating: Rating) {
  return !rating || rating.rating === 'NR' || rating.shortTerm === 'NR';
}

export function isEachNotRated(ratings: Rating[]) {
  return !ratings.length || ratings.every((rating) => isNotRated(rating));
}

function mapRatingValue(rating: Rating): Rating {
  const result = { ...rating };
  if (
    rating &&
    (rating.status === RatingStatus.notAvailable ||
      rating.status === RatingStatus.notAppliedFor ||
      rating.status === RatingStatus.underReview)
  ) {
    result.rating = '-';
  }
  if (
    rating &&
    rating.rating &&
    rating.rating !== 'NR' &&
    rating.status === RatingStatus.expected
  ) {
    result.rating = rating.rating.endsWith('*')
      ? rating.rating
      : rating.rating + '*';
  }

  return result;
}

export function isSeriesInsured(series: BasicSeries) {
  return series.underlyingRatings.length;
}

export function isSeriesEnhanced(series: BasicSeries) {
  return series.insurer;
}

export function getRatingModels(
  seriesList: Series[],
  agencyName: string
): EnhancedFullRating[] {
  const ratings: EnhancedFullRating[] = [];

  seriesList.forEach((series: Series) => {
    // Fetch ratings at series level
    series.ratings
      .filter((rating) => rating.agency === agencyName)
      .map((rating) => ({ ...rating }))
      .forEach((rating: Rating) => {
        const underlyingRating = (series.underlyingRatings || []).find(
          (ur) => ur.agency === agencyName
        );

        const resultRating = mapRatingValue(rating);

        ratings.push({
          rating: {
            ...resultRating,
            enhancement: series.insurer
          },
          underlyingRating: underlyingRating
        });
      });
  });

  return ratings;
}

export function uniqueRatingModels(
  seriesList: Series[],
  agencyName: string
): [EnhancedRating[], EnhancedRating[]] {
  const fullRatings = getRatingModels(seriesList, agencyName);

  const underlyingRatings: EnhancedRating[] = fullRatings.reduce(
    (acc, rating) => {
      if (rating.underlyingRating) {
        acc.push(rating.underlyingRating);
      }
      return acc;
    },
    []
  );

  return [
    reduceRatings(
      fullRatings.map((r) => r.rating).filter((rating) => !isNotRated(rating))
    ),
    reduceRatings(underlyingRatings)
  ];
}

export function uniqueFullRatingModels(
  seriesList: Series[],
  agencyName: string
): EnhancedFullRating[] {
  const uniqueFullRatingPairs = reduceFullRatings(
    getRatingModels(seriesList, agencyName)
  );
  return uniqueFullRatingPairs;
}

function reduceFullRatings(ratings: EnhancedFullRating[]) {
  return ratings.reduce(
    (acc: EnhancedFullRating[], rating: EnhancedFullRating) => {
      if (
        !acc.some(
          (existing) =>
            isEquivalent(existing.rating, rating.rating) &&
            isEquivalent(existing.underlyingRating, rating.underlyingRating)
        )
      ) {
        acc.push(rating);
      }
      return acc;
    },
    []
  );
}

export function extractUniqueRatings(
  seriesList: BasicSeries[],
  agencyName: string
): EnhancedRating[] {
  const allRatings: EnhancedRating[] = seriesList.map((series) => {
    const agencyRating: Rating = series.ratings.find(
      (rating) => rating.agency === agencyName
    );

    if (!agencyRating || isNotRated(agencyRating)) {
      return null;
    }

    return {
      ...mapRatingValue(agencyRating),
      enhancement: series.insurer
    };
  });

  return reduceRatings(allRatings);
}

export function extractUniqueUnderlyingRatings(
  seriesList: BasicSeries[],
  agencyName: string
): Rating[] {
  const allRatings: EnhancedRating[] = seriesList.map((series) => {
    const agencyRating: Rating = series.underlyingRatings
      ? series.underlyingRatings.find((rating) => rating.agency === agencyName)
      : undefined;

    if (!agencyRating || isNotRated(agencyRating)) {
      return null;
    }

    return agencyRating;
  });

  return reduceRatings(allRatings);
}

function reduceRatings(ratings: EnhancedRating[]) {
  return ratings.reduce((acc: EnhancedRating[], rating: EnhancedRating) => {
    if (!acc.some((existing) => isEquivalent(existing, rating))) {
      acc.push(rating);
    }
    return acc;
  }, []);
}

export function uniqueOutlooks(ratings: EnhancedRating[]): RatingOutlook[] {
  return ratings.map((model) => model && model.outlook).filter(onlyUnique);
}

export function getOutlookLabel(outlook: RatingOutlook): OutlookLabel {
  switch (outlook) {
    case RatingOutlook.positive:
      return OutlookLabel.Pos;
    case RatingOutlook.negative:
      return OutlookLabel.Neg;
    case RatingOutlook.steady:
      return OutlookLabel.STB;
    default:
      return OutlookLabel.NONE;
  }
}

export function getEnhancementLabels(
  enhancements: string[],
  forExport: boolean = false
): string[] {
  const labels = [];

  if (enhancements.filter(nonNull).length === 0) {
    if (forExport) {
      labels.push('-');
    } else {
      labels.push('None');
    }
  } else {
    if (enhancements.indexOf(null) > -1) {
      labels.push('Partial');
    }
    if (enhancements.filter(nonNull).length > 1) {
      labels.push('Various');
    }
    if (labels.length === 0) {
      labels.push(enhancements[0]);
    }
  }

  return labels;
}

export function formatFullRatingsForExport(
  ratings: EnhancedFullRating[]
): string[] {
  const allEnhanced =
    ratings.every((r) => !!r.rating.enhancement) && ratings.length > 0;

  return allEnhanced
    ? ratings.map(formatRatingPairForExport)
    : reduceRatings(ratings.filter((r) => !!r.rating).map((r) => r.rating))
      .map((rating) => getRatingValue(rating) || '-')
      .filter(onlyUnique);
}

function formatRatingPairForExport(fullRating: EnhancedFullRating): string {
  const ratingValue = getRatingValue(fullRating.rating) || '-';
  const underlyingRatingValue = takeInParentheses(
    fullRating.underlyingRating && getRatingValue(fullRating.underlyingRating)
  );
  return [ratingValue, underlyingRatingValue]
    .filter((part) => !!part)
    .join(' ');
}

export function getRatingValue(rating: Rating): string {
  if (rating && rating.rating && rating.rating !== 'NR') {
    return rating.rating;
  } else {
    if (rating && rating.shortTerm && rating.shortTerm !== 'NR') {
      return rating.shortTerm;
    } else {
      return null;
    }
  }
}

export function someAgencyHasAllRatingsEnhanced(seriesList: Series[]): boolean {
  return RATING_AGENCIES.some((ratingAgency) => {
    const agencyRatings = extractUniqueRatings(seriesList, ratingAgency.name);

    return (
      agencyRatings.length &&
      agencyRatings.every((rating) => rating && !!rating.enhancement)
    );
  });
}

export function someAgencyHasUnderlyingRating(seriesList: Series[]): boolean {
  return RATING_AGENCIES.some((ratingAgency) => {
    const agencyUnderlyingRatings = extractUniqueUnderlyingRatings(
      seriesList,
      ratingAgency.name
    );

    return agencyUnderlyingRatings.some(nonNull);
  });
}
